import { createContext, useContext } from 'react';

import type { ExpressAPI } from './ExpressApi';

// POST /instagram/resolve is not a cheap observability call. It answers 202 and
// then queues work on the server: one Adobe Express request per scheduled post,
// a backfill, and an Instagram metrics sync that reads up to 30 media items from
// the Meta Graph API and writes a row for each. Never start it more often than
// this, whatever the trigger. Six runs an hour per tab is well below the
// server's own 30 minute floor on counting a look, so no attempt is lost.
//
// It lives here rather than on the provider so a consumer can read it without
// importing the provider, which would drag the whole Express tree with it.
export const RECONCILE_MIN_INTERVAL_MS = 10 * 60 * 1000;

// The floor after a call that did NOT run the work. A failed request queues
// none of the work above, so the long floor would buy nothing. Held on the full
// interval, a failed look also blocked the customer's own leave-and-return
// gesture for ten minutes. That gesture is the one escape route from a stale
// label, so the wait cost the customer and saved the server nothing.
//
// This floor is only a burst guard. A window manager, a browser dialog and the
// URL bar all raise `focus`, so one visit can deliver several in a second. It
// has the same purpose as the floor on the provider's Express-connection focus
// handler (ExpressApiProvider.tsx:192-193). It is longer, because a failing
// route still costs one request each time.
export const RECONCILE_RETRY_MIN_INTERVAL_MS = 60 * 1000;

export type ExpressApiContextType = {
	expressApi: ExpressAPI | null;
	isReady: boolean;
	hasValidInstagramConnection: boolean;
	beginExpressConnection: () => void;
	/** Answers true only when the call ran. A throttle can swallow it. */
	reconcileInstagramPosts: () => Promise<boolean>;
	/** The instant `reconcileInstagramPosts` last ran, or 0 when it never has. A
	 * consumer that waits for a fresh answer reads it to place its own wake past
	 * the throttle window. It lives here because the provider outlives a space
	 * change and a consumer's own mount does not, so a consumer that measured the
	 * window from its mount restarted the wait on every visit. Optional: a
	 * consumer that reads nothing applies no floor, and the swallowed-call retry
	 * covers that. */
	lastReconcileAt?: () => number;
};

export const ExpressApiContext = createContext<ExpressApiContextType | null>(
	null,
);

export const useExpressApiContext = () => {
	const context = useContext(ExpressApiContext);
	if (!context) {
		throw new Error(
			'useExpressApiContext must be used within ExpressApiProvider',
		);
	}
	return context;
};
